import React, { useEffect, useMemo, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const TEAL = '#64ffda'
const NDVI_COLORS = ['#d94e3f', '#f2a261', '#e9c46a', '#8ab17d', '#2a9d8f']
const DEFAULT_CENTER = [13.7367, 100.5232]
const DEFAULT_ZOOM = 5

const ndviFromHealth = (health) => {
  const h = String(health || '').toLowerCase()
  if (h === 'excellent') return 0.82
  if (h === 'good') return 0.65
  if (h === 'fair') return 0.42
  if (h === 'poor') return 0.18
  if (h === 'unhealthy') return -0.22
  return 0.0
}

const ndviColor = (ndvi) => {
  if (ndvi == null) return '#8892b0'
  const i = Math.min(Math.floor((ndvi + 1) * 2.5), 4)
  return NDVI_COLORS[i]
}

function FitBounds({ bounds }) {
  const map = useMap()
  useEffect(() => {
    if (Array.isArray(bounds) && bounds.length) map.fitBounds(bounds, { padding: [36, 36] })
  }, [bounds, map])
  return null
}

export default function MapView({ plantations = [], selectedId, onSelect, staleThreshold }) {
  const [markers, setMarkers] = useState([])

  useEffect(() => {
    const items = Array.isArray(plantations) ? plantations : []
    setMarkers(items.map((p) => ({
      ...p,
      coordinates: [p.lat, p.lon],
      ndvi: p.ndvi != null ? Number(p.ndvi) : ndviFromHealth(p.ndvi_health)
    })))
  }, [plantations])

  const geoJson = useMemo(() => {
    if (!markers.length) return null
    return {
      type: 'FeatureCollection',
      features: markers
        .filter((m) => Array.isArray(m.coordinates) && m.coordinates.length >= 2 && m.coordinates[0] && m.coordinates[1])
        .map((m) => ({
          type: 'Feature',
          properties: { id: m.id, name: m.name, ndvi: m.ndvi, crop_type: m.crop_type, notes: m.notes },
          geometry: { type: 'Point', coordinates: [m.coordinates[1], m.coordinates[0]] },
        })),
    }
  }, [markers])

  const bounds = useMemo(() => {
    if (!geoJson) return null
    try { return geoJson.features.map((f) => f.geometry.coordinates) } catch { return null }
  }, [geoJson])

  return (
    <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} zoomControl={false} attributionControl={false}>
      <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
      {bounds && <FitBounds bounds={bounds} />}
      {geoJson?.features.map((f) => {
        const [lng, lat] = f.geometry.coordinates
        const color = ndviColor(f.properties.ndvi)
        const selected = selectedId === f.properties.id
        const isStale = f.properties.isStale || (f.properties.ndvi_date && staleThreshold && new Date(f.properties.ndvi_date) < staleThreshold)
        const opacity = isStale ? 0.35 : 1
        const markerSize = selected ? 16 : 12
        return (
          <Marker
            key={f.properties.id}
            position={[lat, lng]}
            icon={L.divIcon({
              className: 'marker-ndvi',
              html: `<div style="
                background:${color};
                width:${markerSize}px;
                height:${markerSize}px;
                border-radius:50%;
                opacity:${opacity};
                border:2px solid ${selected ? TEAL : isStale ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.15)'};
                box-shadow:0 0 ${selected ? 18 : 10}px ${color}${isStale ? '22' : '66'}, 0 10px 20px rgba(0,0,0,0.45);
              "></div>`,
              iconSize: [markerSize, markerSize],
              iconAnchor: [markerSize / 2, markerSize / 2],
            })}
            eventHandlers={{ click: () => onSelect?.(f.properties.id) }}
          >
            <Popup>
              <div style={{ color: '#0a192f', minWidth: 180 }}>
                <div style={{ fontWeight: 700 }}>{f.properties.name}</div>
                <div style={{ fontSize: 12, opacity: 0.75 }}>NDVI {f.properties.ndvi.toFixed(3)}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{f.properties.crop_type}</div>
                {f.properties.notes && <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>{f.properties.notes}</div>}
                {isStale && <div style={{ fontSize: 10, color: '#da1e28', marginTop: 4, fontWeight: 500 }}>Stale data</div>}
              </div>
            </Popup>
          </Marker>
        )
      })}
    </MapContainer>
  )
}
